/**
 * The one-line "here's what just happened" toast (previously `Briefing.tsx`'s own `notice`
 * state) — currently only posted by `QuickAddCard`, when a task it created had nowhere
 * obvious to go and was filed to the inbox instead.
 *
 * Pulled out to its own module, rather than kept as page-level state, because `notice` and
 * `add` are portable cards now (D118): a reader can put the "Alerts" box on a different page
 * from the capture form, and the two are still independent React trees fetching their own
 * data (D102's whole point) with no parent in common to hold shared state in. A plain
 * module-level value plus `useSyncExternalStore` is what `settings.ts`'s `useSettings`
 * already does for the same shape of problem (D117) — one poster, any number of readers,
 * none of them needing to be on the same page or mounted at the same time as the other.
 *
 * Session-only on purpose, unlike settings: nothing here is written to `localStorage`, so a
 * reload clears it — a toast that survived a refresh would be a stale one.
 */

import { useSyncExternalStore } from "react";

let current: string | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function postNotice(text: string): void {
  current = text;
  emit();
}

export function dismissNotice(): void {
  current = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): string | null {
  return current;
}

export function useNotice(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** A synchronous read for non-React callers — currently just tests, which have nothing to
 * render `useNotice` into. */
export function currentNotice(): string | null {
  return getSnapshot();
}
